import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type Highcharts from 'highcharts';

import type { BlockThemeConfig, DChartInterface, Service } from 'webapp-types';
import { hexToRgba } from 'webapp/utils/color';
import { formatNumber } from 'webapp/utils/number';

import type { MetricSelectionData } from 'webapp/models-custom/ds-data/output/top';
import type VisualizationHeatmap from 'webapp/models-custom/visualization/heatmap';
import AnnotationsHeatmapHandler from 'webapp/d-chart/annotations/heatmap';
import type DChart from 'webapp/d-chart/d-chart';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import BaseRenderer from 'webapp/d-chart/renderer/base';
import DChartSeries from 'webapp/d-chart/series/series';

// a cell: a period's point, or a filler (with an empty dbxPoint) before the first one
type HeatmapCell = Omit<DChartInterface.ChartDataPoint, 'dbxPoint'> & {
	dbxPoint: DChartPrimaryPoint | Record<string, never>;
	category?: string;
};

// dataOptions as built here: its series' type comes from the chart options
type HeatmapDataOptions = Omit<Highcharts.Options, 'series'> & {
	series: { data: HeatmapCell[]; className: string }[];
};

// Highcharts internals read on render: the cells' shapes
type HeatmapSeriesInternals = Highcharts.Series & {
	data: (Highcharts.Point & { shapeArgs: { width: number; height: number } })[];
};

export default class HeatmapRenderer extends BaseRenderer {
	@service('annotations') declare annotationService: Service.Annotations;
	@service('popup') declare popupService: Service.Popup; // for annotations

	annotationsHandler!: AnnotationsHeatmapHandler;

	private updateCellSize = true;

	constructor(chart: DChart) {
		super(chart);
		this.initAnnotationsHandler();
	}

	override get visualization(): VisualizationHeatmap {
		return super.visualization as VisualizationHeatmap;
	}

	get series(): DChartSeries | null {
		// just one series
		return this.visualization.series;
	}

	get itemsInLine() {
		return this.visualization.actualItemsInLine;
	}

	get lineCount() {
		const startFillerCount = this.visualization.startFillerCount,
			pointCount = this.series?.points.length || 0;
		return Math.ceil((startFillerCount + pointCount) / this.itemsInLine);
	}

	get showValues() {
		return this.visualization.showValues;
	}

	override get legendOptions(): Highcharts.LegendOptions {
		return {
			useHTML: false,
			align: 'right',
			padding: 0,
			margin: 0,
			itemDistance: 0, // ensures the colorAxis right label is not cut off
			navigation: {
				enabled: false,
			},
		};
	}

	override get baseOptions(): Highcharts.Options {
		let options = super.baseOptions;

		const that = this;

		options = this.mergeOptions(options, {
			chart: {
				type: 'heatmap',
				events: {
					load: function (this: Highcharts.Chart) {
						that.annotationsHandler.attachToChart(this);
					},
					render: function (this: Highcharts.Chart, _event: Event) {
						that.annotationsHandler.update();
						that.updateDynamicElementsOnRender(this);
					},
				},
				margin: [0, 0, 0, 0],
			},
			colorAxis: {
				tickAmount: 2, // show only min and max
			},
			yAxis: {
				endOnTick: false, // stretch without padding at the end
				reversed: true,
				startOnTick: false, // stretch without padding at the start
				visible: false,
			},
			plotOptions: {
				series: {
					animation: this.dChart.isAnimated,
					point: {
						events: {
							mouseOver: function (this: Highcharts.Point) {
								that.annotationsHandler.isHoveringOverPoint = true;
								that.annotationsHandler.showAddButton(String(this.category));
							},
						},
					},
					events: {
						mouseOut: function (this: Highcharts.Series) {
							that.annotationsHandler.isHoveringOverPoint = false;
							later(() => {
								// in case we entered diff point / series
								if (!that.annotationsHandler.isHoveringOverPoint) {
									that.annotationsHandler.hideAddButton();
								}
							}, 100);
						},
						click: function (
							this: Highcharts.Series,
							event: Highcharts.SeriesClickEventObject,
						) {
							that.onPointClick(
								(event.point as DChartInterface.ChartHoverPoint).dbxPoint,
								event,
								(event.point as DChartInterface.ChartHoverPoint).graphic!.element,
							);
						},
					},
				},
			},
		});

		return options;
	}

	override get dataOptions(): Highcharts.Options {
		const series = this.series,
			output = this.visualization.output,
			hasData = !!series && !!output;

		const options: HeatmapDataOptions = {
			legend: {
				enabled: hasData,
			},
			series: [],
		};

		if (!hasData) {
			return options;
		}

		const points = series.points;

		{
			// setting color axis
			const colorAxis: Highcharts.ColorAxisOptions = {
				labels: {
					formatter: function () {
						return formatNumber(this.value as number, output.format, {
							scale: output.scale,
						});
					},
				},
			};

			const { min: colorAxisMin, max: colorAxisMax } = DChartSeries.getMinAndMaxValues(
				points.map((p) => p.value),
			);
			colorAxis.softMin = colorAxisMin || 0;
			colorAxis.softMax = colorAxisMax || 1;
			colorAxis.tickPositions = [colorAxis.softMin, colorAxis.softMax];

			options.colorAxis = colorAxis;
		}

		// -------- actual data ---------

		let colCounter = 0,
			rowCounter = 0;

		const itemsInLine = this.itemsInLine,
			startFillerCount = this.visualization.startFillerCount,
			lineCount = this.lineCount,
			allCellsCount = itemsInLine * lineCount, // with fillers
			seriesData: HeatmapCell[] = [],
			annotatablePointsIds: string[] = [];

		for (let i = 0; i < allCellsCount; i++) {
			const pointData: HeatmapCell = {
				x: colCounter,
				y: rowCounter,
				dbxPoint: {},
			};

			const dataPointIndex = i - startFillerCount,
				point = points[dataPointIndex];

			if (point) {
				const category = String(point.timestamp);

				point.colorIndexOrClass = 1; // for tooltip, doesn't matter which as long as it's not 99
				pointData.category = category;
				pointData.value = point.value;
				pointData.name = point.title;
				pointData.dbxPoint = point;
				pointData.dataLabels = {
					enabled: !!this.showValues,
					formatter: () => {
						return point.valueStr;
					},
					useHTML: true,
				};

				annotatablePointsIds.push(category);

				pointData.className =
					typeof point.value === 'number' ? '' : 'highcharts-point-no-data';
			} else {
				// fillers
				pointData.className = 'highcharts-point-no-data';
			}

			seriesData.push(pointData);

			if (colCounter === itemsInLine - 1) {
				rowCounter = rowCounter + 1;
				colCounter = 0;
			} else {
				colCounter = colCounter + 1;
			}
		}

		const seriesClass = series.points[0]?.onClick ? 'series-clickable' : '';

		options.series = [
			{
				data: seriesData,
				className: seriesClass,
			},
		];

		// ---- annotations (icons in left bottom corner of cell) -----
		this.annotationsHandler.annotatablePointsIds = annotatablePointsIds;
		options.annotations = this.annotationsHandler.prepareAnnotationsData();

		const xAxisLabels = this.visualization.xAxisLabels;

		options.xAxis = {
			categories: xAxisLabels,
			labels: {
				// Highcharts reads false as "no auto rotation" (if (autoRotation) …); its types only allow number[]
				autoRotation: false as unknown as number[],
				enabled: xAxisLabels.length > 0,
			},
		};

		return options;
	}

	override get sizeOptions(): Highcharts.Options {
		let options = super.sizeOptions;

		// move legend up for larger resolutions
		const m = this.dChart.sizeModifier,
			y = m >= 15 ? m * -1.2 : 0;

		const colorAxisVisible = m >= 5;

		options = this.mergeOptions(options, {
			chart: {
				marginTop: 0.5 * m,
			},
			colorAxis: {
				labels: {
					y: m,
				},
				visible: colorAxisVisible,
			},
			legend: {
				enabled: colorAxisVisible,
				symbolWidth: m * 15, // width of colorAxis
				symbolHeight: m, // height of the colorAxis bar
				y,
			},
			xAxis: {
				labels: {
					y: 3 + m,
				},
			},
		});

		return options;
	}

	override get themeOptions(): Highcharts.Options {
		let options = super.themeOptions;
		const themeData = this.dChart.themeData,
			colors = themeData.colors || [],
			stepsFallback: BlockThemeConfig.ColorStep[] = [{ range: [0, 1], color: 0 }], // so hc does not throw error. empty can happen when switching visualizations
			steps =
				(themeData.visualizationConfig.steps as BlockThemeConfig.ColorStep[] | undefined) ||
				stepsFallback,
			colorAxis: { stops?: [number, string | undefined][] } = {}; // undefined: a color the theme lacks

		let range = 0;
		colorAxis.stops = [];
		steps.forEach((step) => {
			if (step.range) {
				// step without range is filler's config
				const color = step.opacity
						? hexToRgba(colors[step.color]!, step.opacity)
						: colors[step.color],
					rangeDiff = step.range[1] - step.range[0];

				colorAxis.stops!.push([range, color]);
				range = range + rangeDiff;
			}
		});

		options = this.mergeOptions(options, {
			colorAxis: colorAxis as Highcharts.ColorAxisOptions,
		});

		return options;
	}

	@cached
	get annotationsByAxisTs() {
		return this.getAnnotationsByAxisTsForSeries(this.series);
	}

	getMetricSelectionDataForAxisTs(ts: number): MetricSelectionData[] {
		const granulationLabels = this.series?.granulationLabels || [],
			label = granulationLabels.find((label) => label.ts === ts);

		if (label) {
			const index = granulationLabels.indexOf(label),
				point = this.series?.points[index];
			if (point?.metricSelectionData) {
				return [point.metricSelectionData];
			}
		}

		return [];
	}

	private updateDynamicElementsOnRender(hChart: Highcharts.Chart) {
		const series = hChart.series[0] as HeatmapSeriesInternals | undefined;

		if (!series?.data[0]) {
			return;
		}

		const m = this.dChart.sizeModifier,
			updatedOptions: Highcharts.Options = {},
			// calculating marginBottom (place for xAxis labels and legend at the bottom)
			legendHeight = hChart.legend.group?.element.getBoundingClientRect().height || 0,
			labelGroup = (
				hChart.xAxis[0] as Highcharts.Axis & { labelGroup: Highcharts.SVGElement }
			).labelGroup, // a Highcharts internal
			xAxisLabelHeight = labelGroup.element?.getBoundingClientRect().height || 0,
			marginBottom = Math.round(legendHeight + xAxisLabelHeight + m * 2); // m to ensure space between xAxis labels and colorAxis legend
		let updateChart = false;

		if (hChart.userOptions.chart?.marginBottom !== marginBottom) {
			updateChart = true;

			updatedOptions.chart = { marginBottom };
		}

		// calculating colsize and rowsize to ensure padding around cells
		const userHeatmapPlotOptions = hChart.userOptions.plotOptions?.heatmap,
			cellShape = series.data[0].shapeArgs,
			cellWidth = cellShape.width,
			cellHeight = cellShape.height,
			newCellWidth = cellWidth - m / 2,
			newCellHeight = cellHeight - m / 2,
			colsize = Math.max(0.85, Math.round((newCellWidth / cellWidth) * 100) / 100),
			rowsize = Math.max(0.85, Math.round((newCellHeight / cellHeight) * 100) / 100);
		if (
			this.updateCellSize &&
			(userHeatmapPlotOptions?.colsize !== colsize ||
				userHeatmapPlotOptions?.rowsize !== rowsize)
		) {
			updateChart = true;
			this.updateCellSize = false;

			updatedOptions.plotOptions = { heatmap: { colsize, rowsize } };
		}

		if (updateChart) {
			hChart.update(updatedOptions);
		}
	}

	private initAnnotationsHandler(): void {
		this.annotationsHandler = new AnnotationsHeatmapHandler(this);
	}

	private destroyAnnotationsHandler(): void {
		if (this.annotationsHandler) {
			this.annotationsHandler.destroy();
		}
	}

	// eslint-disable-next-line ember/classic-decorator-hooks
	override destroy(): void {
		super.destroy();
		this.destroyAnnotationsHandler();
	}
}
